import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  ContentState,
  EditorState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import DeleteIcon from "@mui/icons-material/Delete";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";

const FaqManagement = () => {
  const controller = new AbortController();

  const [categories, setCategories] = useState<any[]>([]);
  const [category, setCategory] = useState<any>("0");
  const [subcategory, setSubcategory] = useState<any>("0");
  const [bundle, setBundle] = useState<string | null>(null);
  const [bundles, setBundles] = useState<any[]>([]);
  const [delete_faq, setDeleteFaq] = useState<any>([]);
  const [action, setAction] = useState<boolean>(false);
  const [isFetchFaq, setIsFetchFaq] = useState<boolean>(false);
  const [isAddUpdateFaq, setIsAddUpdateFaq] = useState<boolean>(false);
  const [loadingCategory, setLoadingCategory] = useState<boolean>(false);
  const [deleteCheck, setDeleteCheck] = useState<boolean>(false);
  const faqSectionRef = useRef<HTMLDivElement | null>(null);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [question, setQuestion] = useState<string>("");
  const [editorFaqState, setEditorFaqState] = useState(
    EditorState.createEmpty()
  );
  const [displayFaq, setDisplayFaq] = useState<string>("");
  const [editFaq, setEditFaq] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [authorize, setAuthorize] = useState<boolean>(false);
  const [isBundleLoading, setIsBundleLoading] = useState(false);
  const isAuthorizedPerson = email === "muhammad.ijaz@now.net.pk";

  useEffect(() => {
    fetchCategories();
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setEmail(user.Email);
    }
  }, []);
  console.log(category);
  const fetchCategories = async () => {
    setLoadingCategory(true);
    try {
      const response = await axiosInterceptorInstance.get(
        `/dashboard/ThmPlanCategory`,
        { signal: controller.signal }
      );
      const responseData = JSON.parse(response.data.body)?.Data || [];

      const nestedCategories: any[] = [];

      responseData.forEach((item: any) => {
        const {
          Category_id,
          category_name,
          sub_category_id,
          sub_category_name,
        } = item;

        let category = nestedCategories.find(
          (c: any) => c.category_id === Category_id
        );

        if (!category) {
          category = {
            category_id: Category_id,
            category_name,
            subcategories: [],
          };
          nestedCategories.push(category);
        }

        let subcategory = category.subcategories.find(
          (s: any) => s.sub_category_id === sub_category_id
        );

        if (!subcategory && sub_category_id !== null) {
          subcategory = {
            sub_category_id,
            sub_category_name,
          };
          category.subcategories.push(subcategory);
        }
      });

      setCategories(nestedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategory(false);
    }
  };
  const fetchBundles = async (subCatId: any) => {
    setIsBundleLoading(true);
    try {
      const res = await axiosInterceptorInstance.get(
        "/dashboard/ThmPlanCategoryData",
        {
          params: {
            SubCategoryId: subCatId,
            CategoryId: category,
          },
        }
      );

      const response = JSON.parse(res.data.body);
      const validPlans = response?.Data?.filter(
        (plan: any) => plan.Plan_name && plan.Plan_id
      );

      if (validPlans?.length > 0) {
        const formatted = validPlans?.map((plan: any) => ({
          id: plan.Plan_id,
          name: plan.Plan_name,
        }));
        setBundles(formatted);
      } else {
        setBundles([]);
      }
    } catch (error) {
      console.error("Error fetching bundles:", error);
      setBundles([]);
    } finally {
      setIsBundleLoading(false);
    }
  };

  const fetchFaqs = async () => {
    setIsFetchFaq(true);
    toast.dismiss();
    if (!category || !subcategory) {
      toast.error("Please select all required fields");
      return;
    }

    try {
      const res = await axiosInterceptorInstance.get("/dashboard/ThmGetFaq", {
        params: {
          SubCategoryId: subcategory,
          CategoryId: category,
          PlanId: bundles.length > 0 ? (bundle === "all" ? 0 : bundle) : 0,
        },
      });

      const response = JSON.parse(res.data.body);
      if (response?.Data?.length > 0) {
        setFaqs(response.Data);
      } else {
        toast.info("No Data found");
        ClearFaq();
        setFaqs([]);
      }
    } catch {
      toast.error("Failed to fetch FAQs");
    } finally {
      setShowEditor(true);
      setIsFetchFaq(false);
    }
  };

  const onEditorFaqChange = (newEditorState: EditorState) => {
    setEditorFaqState(newEditorState);
    const value = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    setDisplayFaq(value);
  };

  const handleSave = async () => {
    if (!question || !displayFaq) {
      toast.error("Please enter the required fields");
      return;
    }

    const payload = {
      SubcategoryId: subcategory,
      CategoryId: category,
      PlanId: bundles.length > 0 ? (bundle === "all" ? null : bundle) : null,
      Question: question,
      Answer: displayFaq,
      ...(isEditMode && { FaqId: editFaq.Faqs_Id }),
      ...(isAuthorizedPerson && { IsAuthorize: authorize }),
      AuthorizeBy: email.trim(),
    };

    try {
      setIsAddUpdateFaq(true);
      const res = await axiosInterceptorInstance.post(
        "/dashboard/ThmAddUpdateFaq",
        payload
      );
      const response = JSON.parse(res.data.body);
      toast.info(response.message);
      await new Promise((r) => setTimeout(r, 2000));
      fetchFaqs();
      ClearFaq();
    } catch {
      toast.error("Error saving FAQ");
    } finally {
      setIsAddUpdateFaq(false);
    }
  };

  const handleEdit = (faq: any) => {
    setIsEditMode(true);
    setEditFaq(faq);
    setQuestion(faq.Question);
    setAuthorize(faq.Authorised_Status);
    const blocksFromHTML = convertFromHTML(faq.Answer);
    const contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
    const newEditorState = EditorState.createWithContent(contentState);
    setEditorFaqState(newEditorState);
    setDisplayFaq(faq.Answer);
    setShowEditor(true);
  };

  const handleDelete = async () => {
    try {
      setAction(true);
      const res = await axiosInterceptorInstance.delete(
        `/dashboard/ThmDeleteFaq?faqId=${delete_faq.Faqs_Id}`
      );
      const data = JSON.parse(res.data.body);
      setDeleteCheck(false);
      toast.info(data.message);
      await new Promise((r) => setTimeout(r, 2000));
      fetchFaqs();
      ClearFaq();
    } catch {
      toast.error("Delete failed");
    } finally {
      setAction(false);
    }
  };

  const ClearFaq = () => {
    setQuestion("");
    setAuthorize(false);
    setIsEditMode(false);
    setEditorFaqState(EditorState.createEmpty());
    setDisplayFaq("");
    setEditFaq([]);
  };

  const selectedCategoryObj = categories.find(
    (c) => c.category_id === category
  );
  const filteredSubcategories = selectedCategoryObj?.subcategories || [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Faq Management</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            {/* Category */}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                  Category
                </InputLabel>
                <Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSubcategory("0");
                    setBundle(null);
                    setShowEditor(false);
                    setFaqs([]);
                    setBundles([]);
                  }}
                >
                  <MenuItem value="0">Select Category</MenuItem>
                  {loadingCategory ? (
                    <MenuItem disabled>Loading Categories...</MenuItem>
                  ) : (
                    categories?.map((c) => (
                      <MenuItem key={c.category_id} value={c.category_id}>
                        {c.category_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Subcategory */}
            {category !== "0" && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                    Subcategory
                  </InputLabel>
                  <Select
                    value={subcategory}
                    onChange={(e) => {
                      const selectedSub = e.target.value;
                      setSubcategory(selectedSub);
                      setBundle(null);
                      setShowEditor(false);
                      setFaqs([]);
                      fetchBundles(selectedSub);
                    }}
                  >
                    <MenuItem value="0">Select Subcategory</MenuItem>
                    {filteredSubcategories?.map((s: any) => (
                      <MenuItem
                        key={s.sub_category_id}
                        value={s.sub_category_id}
                      >
                        {s.sub_category_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Bundle */}
            {subcategory !== "0" && bundles.length > 0 && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                    {subcategory == "8" || subcategory == "9"
                      ? "Countries"
                      : "Bundle"}
                  </InputLabel>
                  <Select
                    value={bundle ?? ""}
                    onChange={(e) => {
                      setBundle(e.target.value);
                    }}
                  >
                    <MenuItem value="all">Main</MenuItem>
                    {bundles?.map((b: any) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Fetch FAQs Button */}
            {category !== "0" &&
              subcategory !== "0" &&
              isBundleLoading &&
              bundles.length == 0 && (
                <Grid
                  item
                  xs={4}
                  style={{ margin: "0 auto", textAlign: "center" }}
                >
                  <CircularProgress color="inherit" size={50} />
                </Grid>
              )}
            {category !== "0" &&
              subcategory !== "0" &&
              !isBundleLoading &&
              (bundles.length === 0 ||
                (bundles.length > 0 && bundle !== null)) && (
                <Grid item xs={4} style={{ margin: "0 auto" }}>
                  <FormControl fullWidth>
                    <Button
                      className="dButton"
                      variant="contained"
                      onClick={fetchFaqs}
                      sx={{
                        margin: "0px !important",
                        height: "55px !important",
                      }}
                    >
                      {isFetchFaq ? (
                        <CircularProgress color="inherit" size={30} />
                      ) : (
                        "Fetch FAQs"
                      )}
                    </Button>
                  </FormControl>
                </Grid>
              )}

            {faqs.length > 0 && (
              <div className="faq-list-container">
                <h2>Frequently Asked Questions</h2>
                <Grid item xs={12}>
                  {faqs?.map((faq: any, index: number) => (
                    <div key={index} className="faq-item">
                      <div className="faq-header">
                        <h3 style={{ padding: "0" }}>
                          Q{faq.Faqs_Id}:{faq.Question}
                        </h3>
                        <div className="faq-header">
                          <DriveFileRenameOutlineOutlinedIcon
                            className="edit-button"
                            style={{ marginRight: "20px" }}
                            onClick={() => {
                              handleEdit(faq);
                              setTimeout(() => {
                                const element = faqSectionRef.current;
                                if (element) {
                                  const topOffset =
                                    element.getBoundingClientRect().top +
                                    window.pageYOffset -
                                    100;
                                  window.scrollTo({
                                    top: topOffset,
                                    behavior: "smooth",
                                  });
                                }
                              }, 100);
                            }}
                          ></DriveFileRenameOutlineOutlinedIcon>
                          <DeleteIcon
                            className="edit-button"
                            onClick={() => {
                              setDeleteFaq(faq), setDeleteCheck(true);
                            }}
                          ></DeleteIcon>
                        </div>
                      </div>
                      <p dangerouslySetInnerHTML={{ __html: faq.Answer }} />
                      <hr />
                    </div>
                  ))}
                </Grid>
              </div>
            )}

            {showEditor && (
              <>
                <Grid item xs={12}>
                  <div ref={faqSectionRef}>
                    <h2
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      FAQ Section{" "}
                      <Button
                        className="dButton "
                        variant="contained"
                        style={{ width: "10%", height: "35px" }}
                        onClick={() => ClearFaq()}
                      >
                        Clear
                      </Button>
                    </h2>
                    <Grid item xs={12}>
                      <TextField
                        className="formControl"
                        label="Question"
                        variant="outlined"
                        value={question}
                        required
                        inputProps={{ maxLength: 200 }}
                        onChange={(e: any) => {
                          setQuestion(e.target.value);
                        }}
                      />
                    </Grid>
                    {isAuthorizedPerson && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          style={{ marginLeft: "0px" }}
                          control={
                            <Checkbox
                              checked={authorize}
                              onChange={(e: any) =>
                                setAuthorize(e.target.checked)
                              }
                              inputProps={{ "aria-label": "controlled" }}
                              size="medium"
                            />
                          }
                          label="Authorization"
                          labelPlacement="start"
                        />
                      </Grid>
                    )}
                    <h2>Answer *</h2>
                    <div className="boxShadow editor">
                      <Editor
                        editorState={editorFaqState}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorScrollable"
                        onEditorStateChange={onEditorFaqChange}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid
                  item
                  xs={4}
                  style={{
                    margin: "0 auto",
                  }}
                >
                  <Button
                    variant="contained"
                    className="dButton"
                    onClick={handleSave}
                  >
                    {isAddUpdateFaq ? (
                      <CircularProgress color="inherit" size={30} />
                    ) : (
                      `${isEditMode ? "Update" : "Add"} FAQ`
                    )}
                  </Button>
                </Grid>
              </>
            )}
            <Dialog
              open={deleteCheck}
              onClose={() => setDeleteCheck(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {"Delete FAQ Confirmation"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Would you really like to delete this FAQ?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="contained"
                  className="dButton dButton2 dbuttonSec"
                  onClick={() => setDeleteCheck(false)}
                >
                  No
                </Button>
                <div style={{ width: "30px" }}></div>
                {!action && (
                  <Button
                    variant="contained"
                    className="dButton dButton2"
                    onClick={() => {
                      handleDelete();
                    }}
                    autoFocus
                  >
                    Yes
                  </Button>
                )}
                {action && (
                  <Button className="dButton dButton2" variant="contained">
                    <CircularProgress color="inherit" size={30} />
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </Grid>
        </div>
      </div>
    </motion.div>
  );
};

export default FaqManagement;
